import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useAffiliateTracking from '../../hooks/useAffiliateTracking';
import { getEventBySlug } from '../../services/events';
import CheckoutModal from '../../components/CheckoutModal/CheckoutModal';
import { buildCartFees, getVisibleInventory } from '../../components/CheckoutModal/util';
import { sanitizeEventDetails } from '../../helpers/sanitizeEventDetails';
import { eventImage, eventPlace } from '../../helpers/eventDisplay';
import GlobalContext from '../../context/store';
import { AffContext } from '../../context/AffiliateContext';

function startingTicketTotal(event, company, aff) {
  const available = getVisibleInventory(event?.ticketinventory, aff).filter((ticket) => {
    const remaining = Number(ticket.totalQuantity || 0) - Number(ticket.soldQuantity || 0);
    return remaining > 0;
  });
  if (!available.length) return null;

  const totals = available.map((ticket) => {
    const price = Number(ticket.price || 0);
    if (price <= 0) return 0;
    const fees = buildCartFees(
      [{ id: ticket.ticketName, price, extraFee: Number(ticket.extraFee || 0) }],
      { [ticket.ticketName]: 1 },
      company?.application_fee ?? 3.5,
      0.029,
      0.3,
      event?.host_assist_fee_percent || 0,
    );
    return fees.total;
  });

  return Math.min(...totals);
}

function formatStartingPrice(amount) {
  if (amount <= 0) return 'Free';
  return `$${amount.toFixed(2)}`;
}

function useNarrowScreen(query = '(max-width: 767px)') {
  const [isNarrow, setIsNarrow] = useState(() => {
    try {
      return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
    } catch (error) {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(query);
    const update = () => setIsNarrow(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, [query]);

  return isNarrow;
}

export default function EventDetail() {
  const { slug } = useParams();
  const { company } = useContext(GlobalContext);
  const { aff } = useContext(AffContext);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const isNarrow = useNarrowScreen();
  useAffiliateTracking(event?._id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    let cancelled = false;

    async function loadEvent() {
      setLoading(true);
      setError(false);
      try {
        const response = await getEventBySlug(slug);
        if (!cancelled) setEvent(response || null);
      } catch (loadError) {
        if (!cancelled) {
          setEvent(null);
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadEvent();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <section className="ticket-page">
        <div className="container">
          <div className="ticket-state"><p>Loading event...</p></div>
        </div>
      </section>
    );
  }

  if (!event) {
    return (
      <section className="ticket-page">
        <div className="container">
          <div className="ticket-page__intro">
            <p className="ticket-eyebrow">Events</p>
            <h1 className="ticket-page__title">Event not found</h1>
            <p className="ticket-page__lead">{error ? 'This event is unavailable right now.' : 'That event may have moved or ended.'}</p>
            <Link className="myButn" to="/events">Back to events</Link>
          </div>
        </div>
      </section>
    );
  }

  const image = eventImage(event);
  const place = eventPlace(event);
  const facts = [
    event.start_date_display && ['Date', event.start_date_display],
    event.start_time_display && ['Time', [event.start_time_display, event.timezone_abbr].filter(Boolean).join(' ')],
    place.line && ['Where', place.line],
    event.age && ['Age', event.age],
  ].filter(Boolean);
  const startingAt = startingTicketTotal(event, company, aff);

  return (
    <section className="ticket-page ticket-detail" aria-labelledby="event-title">
      <div className="container">
        <Link className="ticket-back-link" to="/events">All events</Link>
        <div className="ticket-detail__hero">
          {image && (
            <div className="ticket-detail__media">
              <img src={image} alt="" />
            </div>
          )}
          <aside className="ticket-detail__panel">
            <p className="ticket-eyebrow">Upcoming event</p>
            <h1 id="event-title" className="ticket-detail__title">{event.name}</h1>
            {facts.length > 0 && (
              <dl className="ticket-facts">
                {facts.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            )}
            <button type="button" className="myButn ticket-detail__cta" onClick={() => setShowCheckout(true)}>
              Get tickets
            </button>
            <p className="ticket-detail__note">Your QR tickets arrive by email, ready for the door.</p>
          </aside>
          {event.details && (
            <div className="ticket-detail__story">
              <p className="ticket-eyebrow">About this event</p>
              <div className="ticket-detail__body" dangerouslySetInnerHTML={{ __html: sanitizeEventDetails(event.details) }} />
            </div>
          )}
        </div>
      </div>
      {isNarrow && (
        <div className={`ticket-mobile-bar${showCheckout ? ' is-hidden' : ''}`}>
          {startingAt !== null && (
            <p className="ticket-mobile-bar__from">Tickets starting at <strong>{formatStartingPrice(startingAt)}</strong></p>
          )}
          <button type="button" className="myButn" onClick={() => setShowCheckout(true)}>Get tickets</button>
        </div>
      )}
      {showCheckout && <CheckoutModal event={event} onClose={() => setShowCheckout(false)} />}
    </section>
  );
}
